;(() => {
  const namespace = (globalThis.StylistLocator = globalThis.StylistLocator || {})

  // ============================================================================
  // Constants
  // ============================================================================

  const MAP_STYLE = [
    { elementType: 'geometry', stylers: [{ color: '#F0EBE7' }] },
    { elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
    { elementType: 'labels.text.fill', stylers: [{ color: '#523735' }] },
    { elementType: 'labels.text.stroke', stylers: [{ color: '#F0EBE7' }] },
    { featureType: 'administrative', elementType: 'geometry', stylers: [{ color: '#F0EBE7' }] },
    { featureType: 'administrative', elementType: 'geometry.stroke', stylers: [{ color: '#C9B2A6' }] },
    { featureType: 'poi', stylers: [{ visibility: 'off' }] },
    { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#F0EBE7' }] },
    { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#D4C4B8' }] },
    { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#C9B2A6' }] },
    { featureType: 'road', elementType: 'labels.text.fill', stylers: [{ color: '#523735' }] },
    { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#F0EBE7' }] },
    { featureType: 'road.highway', elementType: 'geometry.stroke', stylers: [{ color: '#C9B2A6' }] },
    { featureType: 'transit', stylers: [{ visibility: 'off' }] },
    { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#7CA0C4' }] },
  ]

  const CERTIFICATION_LABELS = {
    all: 'All Certificates',
    certified_salon: 'Certified Salon',
    elite: 'Elite Artist',
    masters: 'Master Technician',
    certified_stylist: 'Certified Stylist',
  }

  // ============================================================================
  // Utility Functions
  // ============================================================================

  /**
   * Escape HTML special characters to prevent XSS.
   * @param {*} value - Value to escape
   * @returns {string} Escaped string
   */
  function escapeHtml(value) {
    if (value == null) return ''
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')
  }

  function toFiniteNumber(value) {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : null
  }

  /**
   * Normalize a string field by trimming whitespace.
   * @param {*} value - Value to normalize
   * @returns {string|null} Trimmed string or null if empty
   */
  function normalizeStringField(value) {
    if (value == null) return null
    const trimmed = String(value).trim()
    return trimmed || null
  }

  /**
   * Parse a coordinate value to a number.
   * @param {*} value - Value to parse
   * @returns {number|null} Parsed coordinate or null if invalid
   */
  function parseCoordinate(value) {
    if (value == null) return null
    if (typeof value === 'string') {
      const trimmed = value.trim()
      return trimmed ? toFiniteNumber(trimmed) : null
    }
    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : null
    }
    return toFiniteNumber(value)
  }

  /**
   * Encode a value for use as a data attribute key.
   * @param {*} value - Value to encode
   * @returns {string} URL-encoded string
   */
  function encodeKey(value) {
    if (!value) return ''
    return encodeURIComponent(String(value))
  }

  /**
   * Decode a URL-encoded data attribute key.
   * @param {*} value - Value to decode
   * @returns {string} Decoded string
   */
  function decodeKey(value) {
    if (!value) return ''
    try {
      return decodeURIComponent(value)
    } catch {
      return value
    }
  }

  // ============================================================================
  // Bottom-sheet drag (mobile)
  // ============================================================================

  const SHEET_DRAG_START_PX = 8
  const SHEET_CLOSE_DISTANCE_RATIO = 0.25
  const SHEET_CLOSE_VELOCITY = 0.5 // px/ms downward flick
  const SHEET_VELOCITY_WINDOW_MS = 100
  const SHEET_MOBILE_MAX_WIDTH = 768

  /**
   * Make a mobile bottom sheet draggable like a native app sheet (e.g. the
   * Instagram comments drawer): pulling down anywhere on the sheet makes it
   * follow the finger, releasing past the distance threshold (or with a
   * downward flick) dismisses it, otherwise it snaps back open. Scrollable
   * content inside the sheet keeps scrolling natively — the drag only takes
   * over when every scroller under the finger is already at the top.
   * Horizontal swipes (carousels, chip rows) are left alone.
   * Touch-only; does nothing on desktop widths.
   * @param {HTMLElement} sheet - Element whose transform moves the sheet; also the drag surface
   * @param {Function} onClose - Called when the drag passes the dismiss threshold
   */
  function attachSheetDrag(sheet, onClose) {
    if (!sheet || sheet._sheetDragBound) return
    sheet._sheetDragBound = true

    let anchorX = 0
    let anchorY = 0
    let baseY = 0
    let tracking = false
    let dragging = false
    let scrollers = []
    let samples = []

    // Touch events keep targeting the touchstart element, so the scroller
    // chain can be resolved once per gesture
    const collectScrollers = (target) => {
      const found = []
      let node = target instanceof Element ? target : null
      while (node && node !== sheet) {
        const overflowY = getComputedStyle(node).overflowY
        if (overflowY === 'auto' || overflowY === 'scroll') found.push(node)
        node = node.parentElement
      }
      return found
    }

    sheet.addEventListener('touchstart', (e) => {
      if (window.innerWidth > SHEET_MOBILE_MAX_WIDTH || e.touches.length !== 1) return
      anchorX = e.touches[0].clientX
      anchorY = e.touches[0].clientY
      tracking = true
      dragging = false
      scrollers = collectScrollers(e.target)
    }, { passive: true })

    sheet.addEventListener('touchmove', (e) => {
      if (!tracking) return
      const touch = e.touches[0]
      if (!dragging) {
        const dx = touch.clientX - anchorX
        const dy = touch.clientY - anchorY
        // Moving up, or content under the finger still has room to scroll up:
        // native scroll wins; ratchet the anchor so a later pull-down from the
        // top of the list is measured fresh (IG-style mid-gesture takeover)
        if (dy <= 0 || scrollers.some((s) => s.scrollTop > 0)) {
          anchorX = touch.clientX
          anchorY = touch.clientY
          return
        }
        if (dy < SHEET_DRAG_START_PX) return
        if (Math.abs(dx) > dy) {
          tracking = false
          return
        }
        dragging = true
        baseY = touch.clientY
        samples = [{ t: e.timeStamp, y: baseY }]
        sheet.style.transition = 'none'
      }
      e.preventDefault()
      sheet.style.transform = `translateY(${Math.max(0, touch.clientY - baseY)}px)`
      samples.push({ t: e.timeStamp, y: touch.clientY })
      while (samples.length > 1 && e.timeStamp - samples[0].t > SHEET_VELOCITY_WINDOW_MS) {
        samples.shift()
      }
    }, { passive: false })

    const endDrag = (e) => {
      if (!tracking) return
      tracking = false
      if (!dragging) return
      dragging = false
      // Clearing the inline overrides and toggling the open class happen in the
      // same frame, so the CSS transition runs from the dragged position
      sheet.style.transition = ''
      sheet.style.transform = ''
      const endY = e.changedTouches[0].clientY
      const dragY = Math.max(0, endY - baseY)
      const oldest = samples[0]
      const elapsed = e.timeStamp - oldest.t
      const velocity = elapsed > 0 ? (endY - oldest.y) / elapsed : 0
      const pastDistance = dragY > sheet.offsetHeight * SHEET_CLOSE_DISTANCE_RATIO
      const flicked = velocity > SHEET_CLOSE_VELOCITY && dragY > SHEET_DRAG_START_PX
      if (pastDistance || flicked) onClose()
    }
    sheet.addEventListener('touchend', endDrag)
    sheet.addEventListener('touchcancel', endDrag)
  }

  // ============================================================================
  // Toast Manager
  // ============================================================================

  /**
   * Simple toast notification manager.
   */
  class ToastManager {
    constructor(container) {
      this.container = container
      this.toasts = new Map()
    }

    show(key, { message, severity = 'info', duration = 2500 }) {
      if (!this.container) return

      let toast = this.toasts.get(key)
      if (!toast) {
        toast = document.createElement('div')
        toast.className = `stylist-locator__toast stylist-locator__toast--${severity}`
        this.container.appendChild(toast)
        this.toasts.set(key, toast)
      }

      toast.textContent = message
      toast.className = `stylist-locator__toast stylist-locator__toast--${severity}`

      if (duration !== Infinity) {
        clearTimeout(toast._timeout)
        toast._timeout = setTimeout(() => this.remove(key), duration)
      }
    }

    remove(key) {
      const toast = this.toasts.get(key)
      if (toast) {
        toast.remove()
        clearTimeout(toast._timeout)
        this.toasts.delete(key)
      }
    }

    clear() {
      Array.from(this.toasts.keys()).forEach((key) => this.remove(key))
    }
  }

  // ============================================================================
  // Google Maps Loader
  // ============================================================================

  const googleMapsPromises = new Map()

  /**
   * Load Google Maps API script.
   * @param {string} apiKey - Google Maps API key
   * @returns {Promise<google>} Resolves with the google object
   */
  function loadGoogleMaps(apiKey) {
    if (globalThis.google && globalThis.google.maps) {
      return Promise.resolve(globalThis.google)
    }

    if (googleMapsPromises.has(apiKey)) {
      return googleMapsPromises.get(apiKey)
    }

    const promise = new Promise((resolve, reject) => {
      const callbackName = `onGoogleMapsReady_${Date.now()}_${Math.floor(Math.random() * 1e6)}`

      globalThis[callbackName] = () => {
        resolve(globalThis.google)
        delete globalThis[callbackName]
      }

      const script = document.createElement('script')
      script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&callback=${callbackName}&libraries=places`
      script.async = true
      script.onerror = () => {
        reject(new Error('Failed to load Google Maps'))
        delete globalThis[callbackName]
      }
      document.head.appendChild(script)
    })

    googleMapsPromises.set(apiKey, promise)
    return promise
  }

  // ============================================================================
  // Exports
  // ============================================================================

  namespace.constants = Object.assign({}, namespace.constants, {
    MAP_STYLE,
    CERTIFICATION_LABELS,
  })

  namespace.utils = Object.assign({}, namespace.utils, {
    escapeHtml,
    normalizeStringField,
    parseCoordinate,
    encodeKey,
    decodeKey,
    attachSheetDrag,
  })

  namespace.ToastManager = namespace.ToastManager || ToastManager
  namespace.loadGoogleMaps = namespace.loadGoogleMaps || loadGoogleMaps
})()
